import { z } from 'zod';
import { addCalendarDays, weekdayOf } from './absence.js';
import { AllowedMimeType, MAX_FILE_BYTES } from './file.js';
import { CalendarDate, TaskPriority } from './project.js';
import { TimeOfDay, minutesFromTimeOfDay } from './work-schedule.js';
/**
 * To-do cá nhân ở trang Việc của tôi — APM-TODO-a, docs/plan-todo.md.
 *
 * Sổ tay riêng của **một** người: không thuộc dự án nào, không ai khác đọc
 * được (plan §2.7). Ba nhóm trên màn hình **suy ra** từ ngày, không lưu (§2.1).
 */
export const TodoRepeatFreq = z.enum(['daily', 'weekly', 'monthly', 'yearly']);
/** Trần của "mỗi N ngày/tuần/tháng/năm" — khớp cột `TINYINT UNSIGNED`. */
export const TODO_REPEAT_MAX_INTERVAL = 99;
/**
 * Quy tắc lặp — plan §2.4. Tính từ **ngày neo** (ngày làm lúc đặt quy tắc),
 * không phải từ ngày làm hiện tại, để 31/1 → 28/2 → 31/3 không trôi thành 28/3.
 *
 * `weekdays` (`0` = Chủ nhật, đúng `weekdayOf`) chỉ có nghĩa với `weekly`; tần
 * suất khác thì bị làm rỗng, để form đổi từ tuần sang tháng không để lại rác.
 */
export const TodoRepeat = z
    .object({
    freq: TodoRepeatFreq,
    interval: z.number().int().min(1).max(TODO_REPEAT_MAX_INTERVAL).default(1),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
})
    .transform((rule) => ({
    ...rule,
    weekdays: rule.freq === 'weekly' ? [...new Set(rule.weekdays)].sort((a, b) => a - b) : [],
}))
    .superRefine((rule, ctx) => {
    if (rule.freq === 'weekly' && rule.weekdays.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['weekdays'], message: 'Chọn ít nhất một thứ' });
    }
});
export const TODO_TITLE_MAX = 500;
/** Cùng trần với mô tả task — cùng một editor sinh ra nó. */
export const TODO_DESCRIPTION_MAX = 20_000;
/** Nhóm Đã xong chỉ trả về chừng này hàng gần nhất; `doneTotal` nói còn bao nhiêu. */
export const TODO_DONE_LIMIT = 50;
/** Trần số file của một to-do. */
export const TODO_MAX_ATTACHMENTS = 20;
/**
 * Cả form tạo/sửa. Sửa cũng gửi **cả form** (`PUT`), không vá từng trường: ba
 * ràng buộc dưới đây đan chéo giữa các trường, và vá một trường thì phải trộn
 * với hàng đang có mới kiểm được.
 */
export const TodoInput = z
    .object({
    title: z.string().trim().min(1).max(TODO_TITLE_MAX),
    description: z.string().max(TODO_DESCRIPTION_MAX).nullable().default(null),
    priority: TaskPriority.default('medium'),
    startDate: CalendarDate,
    /** `null` = chỉ ngày, không có giờ. */
    startTime: TimeOfDay.nullable().default(null),
    dueDate: CalendarDate.nullable().default(null),
    dueTime: TimeOfDay.nullable().default(null),
    repeat: TodoRepeat.nullable().default(null),
    /**
     * File đính kèm — APM-TODO-b. Là **toàn bộ** tập file sau khi lưu: file có ở
     * đây mà chưa gắn thì gắn, đang gắn mà không có ở đây thì gỡ. Hộp tạo tải
     * file lên **trước** khi to-do tồn tại, nên tạo mới cũng đi qua trường này.
     */
    fileIds: z
        .array(z.uuid())
        .max(TODO_MAX_ATTACHMENTS)
        .default([])
        .transform((ids) => [...new Set(ids.map((id) => id.toLowerCase()))]),
})
    .superRefine((todo, ctx) => {
    for (const issue of todoInputIssues(todo))
        ctx.addIssue({ code: 'custom', ...issue });
});
export const CreateTodoInput = TodoInput;
/**
 * Sửa nhanh ngay trên danh sách — APM-TODO-g: mức ưu tiên, ngày làm, ngày hạn.
 *
 * ## Vì sao không dùng `UpdateTodoInput`
 *
 * `UpdateTodoInput` là **cả form**, kể cả `fileIds` là toàn bộ tập file sau khi
 * lưu. Danh sách không có `fileIds` trong tay (`TodoView` chỉ có số đếm), nên
 * sửa nhanh qua đường ấy là gửi `fileIds: []` — tức gỡ hết file đính kèm chỉ
 * vì đổi mức ưu tiên. Đường vá này chỉ nhận ba trường nó thật sự đổi; luật
 * ngày (§2.3) kiểm lại trên state **sau khi ghép**, cùng hàm `todoInputIssues`.
 */
export const PatchTodoInput = z
    .object({
    priority: TaskPriority.optional(),
    startDate: CalendarDate.optional(),
    /** `null` = bỏ hạn (kéo theo bỏ giờ hạn). */
    dueDate: CalendarDate.nullable().optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Cần ít nhất một trường để sửa',
});
export const UpdateTodoInput = TodoInput;
/**
 * Ba ràng buộc của plan §2.3, tách ra hàm để command handler kiểm lại được
 * (không dùng `CHECK`: MySQL 8.0.12 bỏ qua nó).
 */
export function todoInputIssues(todo) {
    const issues = [];
    if (todo.dueDate !== null && todo.repeat !== null) {
        issues.push({ path: ['repeat'], message: 'Việc lặp lại không có hạn hoàn thành' });
    }
    if (todo.dueTime !== null && todo.dueDate === null) {
        issues.push({ path: ['dueTime'], message: 'Chọn ngày hoàn thành trước khi chọn giờ' });
    }
    if (todo.dueDate !== null) {
        if (todo.dueDate < todo.startDate) {
            issues.push({ path: ['dueDate'], message: 'Ngày hoàn thành trước ngày làm' });
        }
        else if (todo.dueDate === todo.startDate &&
            todo.dueTime !== null &&
            todo.startTime !== null &&
            minutesFromTimeOfDay(todo.dueTime) < minutesFromTimeOfDay(todo.startTime)) {
            issues.push({ path: ['dueTime'], message: 'Giờ hoàn thành trước giờ làm' });
        }
    }
    return issues;
}
/**
 * Kéo thả: hai hàng xóm **mới** của dòng vừa kéo (plan §2.6). `null` = đầu /
 * cuối nhóm. Server đặt vị trí ở giữa, nên mỗi cú kéo đổi đúng một hàng.
 */
export const MoveTodoInput = z.object({
    prevId: z.uuid().nullable(),
    nextId: z.uuid().nullable(),
});
export const TodoView = z.object({
    id: z.uuid(),
    title: z.string(),
    priority: TaskPriority,
    startDate: CalendarDate,
    startTime: TimeOfDay.nullable(),
    dueDate: CalendarDate.nullable(),
    dueTime: TimeOfDay.nullable(),
    repeat: TodoRepeat.nullable(),
    /** ISO datetime; `null` = chưa xong. */
    completedAt: z.string().nullable(),
    hasDescription: z.boolean(),
    /** Số file đã tải xong — dòng hiện biểu tượng ghim khi > 0. */
    attachmentCount: z.number().int().nonnegative(),
});
/** Một file đính kèm của to-do. Tải về qua `GET /me/todo-files/:id/url`. */
export const TodoAttachment = z.object({
    id: z.uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
});
export const TodoDetail = TodoView.extend({
    description: z.string().nullable(),
    attachments: z.array(TodoAttachment),
});
export const TodoList = z.object({
    /** "Hôm nay" theo múi giờ tổ chức — mốc mà server đã dùng để chia nhóm. */
    today: CalendarDate,
    /** Đang cần làm: chưa xong, ngày làm ≤ hôm nay. Theo thứ tự kéo thả. */
    now: z.array(TodoView),
    /** Chưa cần làm: chưa xong, ngày làm sau hôm nay. Theo thứ tự kéo thả. */
    later: z.array(TodoView),
    /** Đã xong, mới nhất trước, tối đa `TODO_DONE_LIMIT` hàng. */
    done: z.array(TodoView),
    doneTotal: z.number().int().nonnegative(),
});
/** Nhóm của một to-do — plan §2.1. Hàm thuần, web và API cùng dùng. */
export function todoGroupOf(todo, today) {
    if (todo.completedAt !== null && todo.completedAt !== undefined)
        return 'done';
    return todo.startDate <= today ? 'now' : 'later';
}
// ─── Lặp lại ────────────────────────────────────────────────────────────────
function parts(date) {
    const [year, month, day] = date.split('-').map(Number);
    return { year, month, day };
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
/** Tháng thứ `offset` kể từ tháng của `anchor`, giữ ngày neo, lùi về cuối tháng nếu thiếu. */
function monthsAfter(anchor, offset) {
    const { year, month, day } = parts(anchor);
    const index = year * 12 + (month - 1) + offset;
    const y = Math.floor(index / 12);
    const m = (index % 12) + 1;
    const d = Math.min(day, daysInMonth(y, m));
    return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
/** Thứ Hai của tuần chứa `date` — tuần đếm từ thứ Hai, như lịch Việt Nam. */
function mondayOf(date) {
    return addCalendarDays(date, -((weekdayOf(date) + 6) % 7));
}
/**
 * Lần kế tiếp của một to-do lặp lại — plan §2.4–2.5.
 *
 * Trả về ngày **đầu tiên sau `max(current, today)`** nằm trên lịch neo ở
 * `anchor`. Lấy `max` với hôm nay có chủ đích: một việc hằng ngày trễ ba hôm
 * được tích xong thì lần tới là ngày mai, không phải ba bản trễ nối đuôi.
 *
 * Truyền `today = current` để xem trước chuỗi các lần tới trong form.
 */
export function nextTodoStart(repeat, anchor, current, today) {
    const base = current > today ? current : today;
    if (anchor > base)
        return anchor;
    const step = repeat.interval;
    const gap = daysBetween(anchor, base);
    if (repeat.freq === 'daily') {
        return addCalendarDays(anchor, (Math.floor(gap / step) + 1) * step);
    }
    if (repeat.freq === 'weekly') {
        const anchorWeek = mondayOf(anchor);
        // Trần: đủ để đi qua `step` tuần trống rồi thêm một tuần có ngày.
        for (let i = 1; i <= 7 * (step + 1); i += 1) {
            const candidate = addCalendarDays(base, i);
            const weeks = daysBetween(anchorWeek, mondayOf(candidate)) / 7;
            if (weeks % step === 0 && repeat.weekdays.includes(weekdayOf(candidate)))
                return candidate;
        }
        // Không tới được nếu `weekdays` khác rỗng — Zod đã chặn rỗng.
        return addCalendarDays(base, 7 * step);
    }
    const monthStep = repeat.freq === 'monthly' ? step : step * 12;
    const a = parts(anchor);
    const b = parts(base);
    const monthsGap = (b.year - a.year) * 12 + (b.month - a.month);
    let k = Math.max(0, Math.floor(monthsGap / monthStep));
    let candidate = monthsAfter(anchor, k * monthStep);
    while (candidate <= base) {
        k += 1;
        candidate = monthsAfter(anchor, k * monthStep);
    }
    return candidate;
}
/** `count` lần tới kể từ `start` — để form xem trước. */
export function upcomingTodoStarts(repeat, start, count) {
    const dates = [start];
    while (dates.length < count) {
        const last = dates[dates.length - 1];
        dates.push(nextTodoStart(repeat, start, last, last));
    }
    return dates;
}
// ─── File đính kèm (APM-TODO-b) ────────────────────────────────────────────
/**
 * Xin URL tải lên cho một file to-do. Giống `PresignUploadInput` bỏ `projectId`:
 * file to-do không thuộc dự án nào, và quyền duy nhất là "người tải lên là tôi".
 */
export const TodoFilePresignInput = z.object({
    filename: z.string().min(1).max(255),
    mimeType: AllowedMimeType,
    sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
    checksumSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'checksum phải là SHA-256 dạng hex thường, 64 ký tự'),
});
/**
 * To-do đã **quá hạn** chưa — APM-TODO-e. Một hàm cho mọi nơi hiện to-do (tab
 * To-do, "Cập nhật nhanh", Mini App Telegram), để ba chỗ không tô đỏ khác nhau.
 *
 * - Có hạn: qua ngày hạn, hoặc đúng ngày hạn mà đã qua giờ hạn (nếu có giờ).
 *   Hạn không kèm giờ thì hết **cả ngày** hạn mới quá.
 * - Lặp lại (không có hạn, plan §2.3): lần hiện tại lẽ ra làm từ một ngày
 *   **trước hôm nay** mà chưa tích — tức đã lỡ một lần.
 * - Không hạn, không lặp: không bao giờ quá hạn.
 *
 * `today` và `nowMinute` theo múi giờ **tổ chức** (bẫy APM-TZ) — người gọi
 * truyền vào, để hàm thuần và test được.
 */
export function isTodoOverdue(todo, today, nowMinute) {
    if (todo.completedAt !== null)
        return false;
    if (todo.dueDate !== null) {
        if (todo.dueDate < today)
            return true;
        return (todo.dueDate === today &&
            todo.dueTime !== null &&
            minutesFromTimeOfDay(todo.dueTime) < nowMinute);
    }
    return todo.repeat !== null && todo.startDate < today;
}
//# sourceMappingURL=todo.js.map